import { useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRealtimePostgresChanges } from './useRealtimePostgresChanges';
import { fireDesktopNotification } from '@/lib/desktop-notifications';
import { avgRunPostScore } from '@/lib/runScore';
import i18n from '@/lib/i18n';

export const OPTIMIZATION_SUCCESS_EVENT = 'foai:show-optimization-success';

export interface OptimizationSuccessEventDetail {
  feedId: string;
  completed: number;
  failed: number;
  /**
   * Vorher/Nachher DIESES Laufs. Der Empfaenger (`useOptimizationJob`) liest
   * heute nur `completed`/`failed` — die beiden Werte stehen hier, weil sie
   * dieselbe Quelle wie die Meldung selbst haben und beim naechsten Leser
   * sonst wieder von Hand beschafft wuerden.
   */
  preScore: number | null;
  postScore: number | null;
}

/**
 * Global Realtime listener that fires a desktop notification once the
 * backend-triggered post-optimization score calculation completes.
 *
 * Because the backend automatically enqueues the post-optimization score
 * right after an optimization job succeeds (see
 * process-optimization-queue/job-lifecycle.ts), the completion of that
 * score job is a strict superset signal for "the optimization flow is done".
 * We therefore only need a single Realtime subscription.
 *
 * Mounted once at app level inside the AuthProvider tree.
 */
export function useJobCompletionNotifications(): void {
  const { user } = useAuth();
  const firedJobsRef = useRef<Set<string>>(new Set());

  const userId = user?.id ?? null;

  useRealtimePostgresChanges(
    userId ? 'job-completion-notifications' : null,
    userId
      ? { event: 'UPDATE', table: 'score_calculation_jobs', filter: `user_id=eq.${userId}` }
      : null,
    (payload) => {
      if (!userId) return;
      const row = payload.new as {
        id: string;
        feed_id: string;
        status: string;
        score_type: string;
        source_optimization_job_id?: string | null;
      };

      if (row.status !== 'completed') return;
      if (row.score_type !== 'post_optimization') return;
      // A2-NEU-3: seit "Neu pruefen" und der Score nach einem Sync auf
      // optimierten Feeds `post_optimization` bestellen, gibt es Nachher-Jobs
      // OHNE Optimierungslauf. Nur der vom Backend nach einem Lauf bestellte Job
      // traegt `source_optimization_job_id`. Fehlt das Feld ganz im Payload,
      // bleibt das bisherige Verhalten, damit die Meldung nie still ausfaellt.
      if ('source_optimization_job_id' in row && row.source_optimization_job_id == null) return;
      // There used to be a `payload.old.status === 'completed'` guard here. It was dead
      // code and always has been: score_calculation_jobs has REPLICA IDENTITY DEFAULT,
      // so Postgres writes only the identity columns to the WAL for the old tuple, and
      // walrus therefore fills old_record with the primary key alone. `old.status` was
      // permanently undefined, so the guard never once returned.
      //
      // Removed rather than repaired. Repairing it would mean REPLICA IDENTITY FULL on a
      // table that gets a progress UPDATE per score batch, to dedupe a desktop
      // notification that firedJobsRef already dedupes for the lifetime of the page.
      // Residual risk being accepted: after a reload the ref is empty, so a late UPDATE
      // on an already-completed job could re-notify. If that ever shows up in practice,
      // the fix is FULL on this table (plus restoring the guard) — not a bigger ref.
      if (firedJobsRef.current.has(row.id)) return;
      firedJobsRef.current.add(row.id);

      // Explicit catch: this runs inside a Realtime callback, so a rejection would
      // surface as an unhandled promise rejection with nobody to attribute it to.
      void handlePostScoreCompleted(row.feed_id, userId).catch((err) => {
        console.error('useJobCompletionNotifications: post-score handling failed', err);
      });
    },
  );
}

async function handlePostScoreCompleted(feedId: string, userId: string): Promise<void> {
  // Find the matching optimization_job: completed recently for this user + feed.
  const { data: optJob } = await supabase
    .from('optimization_jobs')
    .select('id, completed_products, failed_products, completed_at, pre_optimization_avg_score')
    .eq('user_id', userId)
    .eq('feed_id', feedId)
    .eq('status', 'completed')
    .gt('completed_at', new Date(Date.now() - 15 * 60 * 1000).toISOString())
    .order('completed_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (!optJob) return;

  const completed = optJob.completed_products ?? 0;
  const failed = optJob.failed_products ?? 0;
  if (completed === 0) return;

  // "{{count}} Produkte optimiert · Quality Score {{pre}} → {{post}}": die
  // Zahl links zaehlt DIESEN Lauf (Job-Zeile), also muessen es die beiden
  // Werte rechts auch tun. Vorher las diese Stelle
  // `product_scores` nur auf `feed_id` gefiltert und schnitt beide Seiten ueber
  // ALLE je gescorten Produkte des Feeds — dieselbe feed-weite Groesse unter
  // einer lauf-bezogenen Beschriftung, gegen die Welle 3 den Erfolgs-Ring
  // umgebaut hat. Dazu kam die PostgREST-Kappung bei 1000 Zeilen: auf einem
  // Feed mit 10.756 Produkten war der Schnitt der einer unbestimmten
  // Teilmenge, und ein Nutzer, der 200 schwache Produkte optimiert, bekam
  // trotzdem den kaum bewegten Feed-Schnitt gemeldet.
  //
  // Vorher-Seite: `pre_optimization_avg_score` ist genau der Schnitt der
  // Produkte dieses Jobs (`enqueue-optimization-batches` snapshottet
  // `avg_latest_total_score(...)`) — dieselbe Quelle, die das Erfolgsbild
  // benutzt. Nachher-Seite: dieselbe Grundmenge, aber ueber
  // `optimization_events` dieses Jobs statt ueber die Spalte `product_ids` der
  // Job-Zeile. Die schreiben beide Erzeuger LEER (129 von 129 Jobs seit
  // 2026-05), `postScore` war deshalb strukturell immer null und
  // `bodyWithScore` toter Code — die Meldung hat seit dem Umbau nie einen Score
  // genannt. Begruendung und Obergrenze: `lib/runScore.ts`.
  //
  // `completed_at` ist der Cutoff des Frische-Praedikats (Welle 13): gezaehlt
  // werden nur Scores, die NACH dem Abschluss dieses Laufs berechnet wurden.
  // Ohne den Cutoff haette die Meldung im Rate-Limit-/Suppression-Fenster den
  // ALTEN feed-weiten Score derselben Produkte als Ertrag dieses Laufs genannt.
  // Stale-only ergibt null, und null faellt hier ehrlich auf `bodyNoScore`.
  // `completed_at` ist durch das `.gt('completed_at', …)`-Praedikat oben
  // garantiert gesetzt; der Guard ist reine Defensive.
  const preScore = optJob.pre_optimization_avg_score != null
    ? Number(optJob.pre_optimization_avg_score)
    : null;
  const postScore = optJob.id && optJob.completed_at
    ? await avgRunPostScore(optJob.id, optJob.completed_at)
    : null;

  const t = i18n.getFixedT(null, 'dashboard');
  const title = t('desktopNotification.titleDone');
  const body =
    preScore != null && postScore != null
      ? t('desktopNotification.bodyWithScore', {
          count: completed,
          pre: Math.round(preScore),
          post: Math.round(postScore),
        })
      : t('desktopNotification.bodyNoScore', { count: completed });

  fireDesktopNotification({
    title,
    body,
    icon: '/favicon.svg',
    tag: `foai-optimization-${feedId}`,
    onClick: () => {
      const detail: OptimizationSuccessEventDetail = {
        feedId,
        completed,
        failed,
        preScore,
        postScore,
      };
      window.dispatchEvent(new CustomEvent(OPTIMIZATION_SUCCESS_EVENT, { detail }));
    },
  });
}
